import api from '../../../../services/Api';
import ArquivoOperacaoService from '../../../../services/ArquivoOperacaoService';

import {
  AcaoConfirmarPdf,
  IAuditFile,
  IAuditInputType,
  IAuditTrailEvent,
  IAuditoriaExtratoGrupo,
  IAuditoriaExtratoLinha,
  IAuditoriaExtratoPagina,
  IAuditoriaExtratoResumo,
  IContaContabilBancaria,
  IImportacaoContexto,
} from '../interfaces/IAuditoriaEntradaDados';

const BASE = 'auditoria-entrada-dados';

export interface IResultadoAcao {
  sucesso: boolean;
  mensagem: string;
  arquivo?: IAuditFile;
}

const mensagemDeErro = (err: any, padrao: string): string => err?.response?.data?.message || padrao;

// O arquivo sobe direto para o bucket pela URL assinada (mesmo caminho do extrato do cartão) e só
// depois é registrado na auditoria — devolve o nome gerado no bucket.
const enviarParaBucket = async (file: File, onProgress?: (percent: number) => void): Promise<string> => {
  onProgress?.(10);
  const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(
    file.name,
    file.type || 'application/octet-stream'
  );
  onProgress?.(30);
  await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
  onProgress?.(90);
  return generatedFileName;
};

// DEVN3-662 — Central de Entrada de Dados da Auditoria (`api/auditoria-entrada-dados`).
export const AuditoriaEntradaDadosService = {
  async listarTiposEntrada(): Promise<IAuditInputType[]> {
    const { data } = await api.get<IAuditInputType[]>(`${BASE}/input-types`);
    return data || [];
  },

  async listarContasBancarias(esteiraId: number): Promise<IContaContabilBancaria[]> {
    if (!esteiraId) return [];
    const { data } = await api.get<IContaContabilBancaria[]>(`${BASE}/contas`, { params: { esteiraId } });
    return data || [];
  },

  async listarArquivos(esteiraId: number, cartaoId?: number): Promise<IAuditFile[]> {
    if (!esteiraId) return [];
    const { data } = await api.get<IAuditFile[]>(`${BASE}/files`, {
      params: { esteiraId, ...(cartaoId ? { cartaoId } : {}) },
    });
    return data || [];
  },

  async consultarArquivo(auditFileId: number): Promise<IAuditFile> {
    const { data } = await api.get<IAuditFile>(`${BASE}/files/${auditFileId}`);
    return data;
  },

  async buscarHistorico(auditFileId: number): Promise<IAuditTrailEvent[]> {
    const { data } = await api.get<IAuditTrailEvent[]>(`${BASE}/files/${auditFileId}/history`);
    return data || [];
  },

  async importarArquivo(
    contexto: IImportacaoContexto,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<IAuditFile> {
    const nomeGerado = await enviarParaBucket(file, onProgress);
    const { data } = await api.post<IAuditFile>(`${BASE}/files`, {
      esteiraId: contexto.esteiraId,
      cartaoId: contexto.cartaoId,
      tipoEntrada: contexto.auditInputTypeCode,
      nomeGerado,
      nomeOriginal: file.name,
      mimeType: file.type || null,
      tamanhoBytes: file.size,
    });
    onProgress?.(100);
    return data;
  },

  async reprocessar(auditFileId: number): Promise<IResultadoAcao> {
    try {
      const { data } = await api.post<IAuditFile>(`${BASE}/files/${auditFileId}/reprocess`);
      return { sucesso: true, mensagem: 'Reprocessamento solicitado.', arquivo: data };
    } catch (err) {
      return { sucesso: false, mensagem: mensagemDeErro(err, 'Não foi possível solicitar o reprocessamento.') };
    }
  },

  async excluirArquivo(auditFileId: number): Promise<IResultadoAcao> {
    try {
      const { data } = await api.delete<{ message: string }>(`${BASE}/files/${auditFileId}`);
      return { sucesso: true, mensagem: data?.message || 'O arquivo foi removido da auditoria.' };
    } catch (err) {
      return { sucesso: false, mensagem: mensagemDeErro(err, 'Não foi possível excluir o arquivo.') };
    }
  },

  async substituirArquivo(
    auditFileId: number,
    novoArquivo: File,
    onProgress?: (percent: number) => void
  ): Promise<IResultadoAcao> {
    try {
      const nomeGerado = await enviarParaBucket(novoArquivo, onProgress);
      const { data } = await api.post<IAuditFile>(`${BASE}/files/${auditFileId}/replace`, {
        nomeGerado,
        nomeOriginal: novoArquivo.name,
        mimeType: novoArquivo.type || null,
        tamanhoBytes: novoArquivo.size,
      });
      onProgress?.(100);
      return { sucesso: true, mensagem: 'A nova versão do arquivo foi recebida e será processada.', arquivo: data };
    } catch (err) {
      return { sucesso: false, mensagem: mensagemDeErro(err, 'Não foi possível substituir o arquivo.') };
    }
  },

  // PDF — depois da previsão de custo: processar (começa a leitura por IA) ou descartar.
  async decidirPdf(auditFileId: number, processar: boolean): Promise<IResultadoAcao> {
    try {
      await api.post(`${BASE}/files/${auditFileId}/pdf/decidir`, { processar });
      return {
        sucesso: true,
        mensagem: processar ? 'Leitura do PDF por IA iniciada.' : 'PDF descartado. Nenhum custo foi gerado.',
      };
    } catch (err) {
      return { sucesso: false, mensagem: mensagemDeErro(err, 'Não foi possível registrar a decisão.') };
    }
  },

  // PDF — leitura que não fechou com os saldos: importar mesmo assim, reler ou descartar.
  async confirmarPdf(auditFileId: number, acao: AcaoConfirmarPdf): Promise<IResultadoAcao> {
    try {
      await api.post(`${BASE}/files/${auditFileId}/pdf/confirmar`, { acao });
      const mensagens: Record<AcaoConfirmarPdf, string> = {
        IMPORTAR: 'Importação confirmada. O processamento continua em seguida.',
        RELER: 'Releitura das páginas divergentes solicitada.',
        DESCARTAR: 'PDF descartado.',
      };
      return { sucesso: true, mensagem: mensagens[acao] };
    } catch (err) {
      return { sucesso: false, mensagem: mensagemDeErro(err, 'Não foi possível confirmar o extrato.') };
    }
  },

  // ── Resultado do cruzamento extrato × contabilidade (por conta) ──────────────────────────

  async buscarResumo(esteiraId: number, cartaoId: number): Promise<IAuditoriaExtratoResumo> {
    const { data } = await api.get<IAuditoriaExtratoResumo>(`${BASE}/resultado`, { params: { esteiraId, cartaoId } });
    return data;
  },

  async buscarConciliados(
    esteiraId: number,
    cartaoId: number,
    page: number,
    perPage: number
  ): Promise<IAuditoriaExtratoPagina<IAuditoriaExtratoGrupo>> {
    const { data } = await api.get<IAuditoriaExtratoPagina<IAuditoriaExtratoGrupo>>(`${BASE}/resultado/conciliados`, {
      params: { esteiraId, cartaoId, page, perPage },
    });
    return data;
  },

  async buscarExtratoPendente(
    esteiraId: number,
    cartaoId: number,
    page: number,
    perPage: number
  ): Promise<IAuditoriaExtratoPagina<IAuditoriaExtratoLinha>> {
    const { data } = await api.get<IAuditoriaExtratoPagina<IAuditoriaExtratoLinha>>(
      `${BASE}/resultado/extrato-pendente`,
      { params: { esteiraId, cartaoId, page, perPage } }
    );
    return data;
  },

  async buscarContabilidadePendente(
    esteiraId: number,
    cartaoId: number,
    page: number,
    perPage: number
  ): Promise<IAuditoriaExtratoPagina<IAuditoriaExtratoLinha>> {
    const { data } = await api.get<IAuditoriaExtratoPagina<IAuditoriaExtratoLinha>>(
      `${BASE}/resultado/contabilidade-pendente`,
      { params: { esteiraId, cartaoId, page, perPage } }
    );
    return data;
  },
};
